import { useCallback, useEffect, useRef, useState } from 'react'

import type { ProcessStep } from './ProcessSteps'

interface ProcessTiming {
  stepMs: number
  holdMs: number
}

export function useProcessRun() {
  const [visibleSteps, setVisibleSteps] = useState<ProcessStep[]>([])
  const [running, setRunning] = useState(false)
  const timers = useRef(new Set<number>())
  const generation = useRef(0)

  const cancel = useCallback(() => {
    generation.current += 1
    timers.current.forEach((timer) => window.clearTimeout(timer))
    timers.current.clear()
    setRunning(false)
    setVisibleSteps([])
  }, [])

  useEffect(() => cancel, [cancel])

  const wait = useCallback((duration: number) => new Promise<void>((resolve) => {
    const timer = window.setTimeout(() => {
      timers.current.delete(timer)
      resolve()
    }, duration)
    timers.current.add(timer)
  }), [])

  const run = useCallback(async (steps: ProcessStep[], timing: ProcessTiming): Promise<boolean> => {
    cancel()
    const current = generation.current
    if (steps.length === 0) return true
    setRunning(true)
    setVisibleSteps([steps[0]!])
    for (let index = 1; index < steps.length; index += 1) {
      await wait(timing.stepMs)
      if (generation.current !== current) return false
      setVisibleSteps(steps.slice(0, index + 1))
    }
    await wait(timing.holdMs)
    if (generation.current !== current) return false
    setRunning(false)
    setVisibleSteps([])
    await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()))
    return generation.current === current
  }, [cancel, wait])

  return { visibleSteps, running, run, cancel }
}
